import { onBlockLoaded } from "@plaudit/frontend-apis";
import apiFetch from "@wordpress/api-fetch";

function getQuoteProducts(): Record<string, number> {
	return JSON.parse(localStorage.getItem("msa/request-a-quote/products") || '{}') as Record<string, number>;
}

function quoteHasProducts(products: Record<string, number>): boolean {
	return Object.keys(products).length > 0;
}

onBlockLoaded({
	blockOrClassName: "gf-quote-request-products-field-wrapper",
	async onAdd(element) {
		const tableBody = element.querySelector<HTMLTableSectionElement>('.client-side-interface tbody')!;
		const realInput = element.querySelector<HTMLInputElement>("input.gf-quote-request-products-field")!;
		realInput.value = JSON.stringify(getQuoteProducts());

		let hydrated = false;

		const buildTableBody = async () => {
			const quoteProducts = getQuoteProducts();
			realInput.value = JSON.stringify(quoteProducts);

			// Email/prefetch hits almost never have localStorage products — skip the REST round-trip.
			if (!quoteHasProducts(quoteProducts)) {
				tableBody.replaceChildren();
				element.classList.remove('loading');
				hydrated = true;
				return;
			}

			element.classList.toggle('loading', true);

			const rawProductData: {image: string, name: string, id: string, quantity: number}[] = await apiFetch({
				path:   '/msa/quote-request-products-field/v1/product-data',
				method: 'POST',
				data:   {'quote-products': quoteProducts}
			});

			const domParser = new DOMParser();
			const productData = rawProductData.map(({image, name, id, quantity}) => ({
				image: domParser.parseFromString(image, 'text/html').body.firstElementChild!, name:  domParser.parseFromString(name, 'text/html').body.firstElementChild!, id, quantity
			}));

			const template = document.getElementById("quote-request-product") as HTMLTemplateElement;
			tableBody.replaceChildren(...productData.map(({image, name, id, quantity}) => {
				const row = (template.content.cloneNode(true) as DocumentFragment).firstElementChild as HTMLTableRowElement;
				for (const slot of row.querySelectorAll("slot[name='product-image']")) {
					slot.replaceWith(image.cloneNode(true));
				}
				for (const slot of row.querySelectorAll("slot[name='product-name']")) {
					slot.replaceWith(name.cloneNode(true));
				}
				row.setAttribute("data-product-id", id);
				const input = row.querySelector<HTMLInputElement>("input.product-quantity")!;
				input.value = quantity.toString();
				input.addEventListener('change', () => {
					const value = input.value.trim();
					if (value.length > 0) {
						updateQuote(id, parseInt(value), realInput);
					}
				});
				row.querySelector<HTMLButtonElement>("button.remove-product")?.addEventListener('click', () => {
					updateQuote(id, 0, realInput);
					row.remove();
				});
				return row;
			}));
			element.classList.remove('loading');
			hydrated = true;
		};

		// Load immediately when storage has products; empty storage skips the REST call above.
		await buildTableBody();

		document.addEventListener("msa/region-locked-products/quote-update", async e => {
			realInput.value = JSON.stringify(getQuoteProducts());
			if (e.target !== realInput) {
				// Quote products were updated; update the table body.
				const quoteProducts = getQuoteProducts();
				const missedProductIds = new Set(Object.keys(quoteProducts));
				for (const tr of tableBody.querySelectorAll("tr")) {
					const productId = tr.getAttribute("data-product-id")!;
					missedProductIds.delete(productId);
					if (!(productId in quoteProducts)) {
						tr.remove();
					} else {
						tr.querySelector<HTMLInputElement>("input.product-quantity")!.value = quoteProducts[productId].toString();
					}
				}
				if (missedProductIds.size !== 0 || (!hydrated && quoteHasProducts(quoteProducts))) {
					await buildTableBody();
				} else if (!quoteHasProducts(quoteProducts)) {
					tableBody.replaceChildren();
					element.classList.remove('loading');
				}
			}
		});
	}
});

document.addEventListener('visibilitychange', () => {
	document.dispatchEvent(new Event("msa/region-locked-products/quote-update"));
});

function updateQuote(productId: string, quantity: number, eventTarget: HTMLElement) {
	const products = getQuoteProducts();
	if (quantity === 0) {
		delete products[productId];
	} else {
		products[productId] = quantity;
	}
	localStorage.setItem("msa/request-a-quote/products", JSON.stringify(products));
	eventTarget.dispatchEvent(new Event("msa/region-locked-products/quote-update", {bubbles: true}));
}
